import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon, InfoIcon, PenLineIcon } from 'lucide-react';
import { ListPageLayout } from '../components/lists/ListPageLayout';
import { LoanDetailDrawer } from '../components/loans/LoanDetailDrawer';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { useToast } from '../contexts/ToastContext';
import { loans } from '../data/loans';
import type { CommunicationSubject, Loan } from '../types/portal';
const filters = [{
  id: 'all',
  label: 'All active'
}, {
  id: 'update',
  label: 'Needs an update'
}, {
  id: 'ctc',
  label: 'Clear to close'
}, {
  id: 'processing',
  label: 'In processing'
}];
export function MyLoansPage() {
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeLoan, setActiveLoan] = useState<Loan | null>(null);
  const [subject, setSubject] = useState<CommunicationSubject | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 600);
    return () => window.clearTimeout(timer);
  }, []);
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return loans.filter(loan => {
      const matchesFilter = filter === 'all' ? true : filter === 'update' ? loan.needsUpdate : filter === 'ctc' ? loan.milestone === 'Clear to Close' : loan.milestone === 'Processing' || loan.milestone === 'Underwriting' || loan.milestone === 'Conditions';
      const matchesSearch = term.length === 0 || loan.borrower.toLowerCase().includes(term) || loan.id.toLowerCase().includes(term) || loan.realtor.toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [filter, search]);
  const filterChips = filters.map(item => ({
    ...item,
    count: item.id === 'all' ? loans.length : item.id === 'update' ? loans.filter(loan => loan.needsUpdate).length : item.id === 'ctc' ? loans.filter(loan => loan.milestone === 'Clear to Close').length : loans.filter(loan => ['Processing', 'Underwriting', 'Conditions'].includes(loan.milestone)).length
  }));
  return <>
      <ListPageLayout title="My Loans" subtitle="Active transactions handed off to in-house processing." banner={<div className="flex items-start gap-3 rounded-2xl border border-teal-200 bg-teal-50 p-4" data-id="element-714" data-mp="256w67248bz6j">
            <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" aria-hidden="true" data-id="element-715" data-mp="207t0rgkc7qy1" />
            <p className="text-sm text-teal-900" data-id="element-716" data-mp="1v9rigxuqjvnm">
              Processing owns the file once a contract is in. These records are
              read-only — open a loan to see loan details, processor notes,
              appraisal status, and documents. Your job here is keeping the
              borrower and Realtor informed.
            </p>
          </div>} searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search by borrower, loan number, or Realtor" filters={filterChips} activeFilter={filter} onFilterChange={setFilter} loading={loading} isEmpty={visible.length === 0} emptyTitle="No active transactions here" emptyMessage="Loans appear once a purchase contract is received and processing takes the file." resultLabel={`Showing ${visible.length} of ${loans.length} active loans`} data-id="element-713" data-mp="297g3c5osz2so">
        <ul className="divide-y divide-navy-100" data-id="element-717" data-mp="1c54uyz88o8sk">
          {visible.map(loan => <li key={loan.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-718" data-mp="ene9wds4ddqd">
              <button type="button" onClick={() => setActiveLoan(loan)} className="group min-w-0 flex-1 text-left" data-id="element-719" data-mp="2lb55p4tpn1fl">
                <div className="flex flex-wrap items-center gap-2" data-id="element-720" data-mp="2v1ga0shb3cn0">
                  <p className="text-sm font-semibold text-navy-900 group-hover:text-brand-700" data-id="element-721" data-mp="2v3t1pxcv91d9">
                    {loan.borrower}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700 ring-1 ring-brand-200" data-id="element-722" data-mp="341d2z6aqaxnv">
                    {loan.milestone}
                  </span>
                  {loan.needsUpdate ? <span className="inline-flex items-center rounded-full bg-warn-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-warn-700 ring-1 ring-warn-200" data-id="element-723" data-mp="3917th8qldl8e">
                      Borrower update due
                    </span> : null}
                </div>
                <p className="mt-1 text-sm text-navy-700" data-id="element-724" data-mp="3qajsqtaxahrk">
                  {loan.amount} · {loan.program} · {loan.closingLabel}
                </p>
                <p className="mt-1 text-xs text-navy-500" data-id="element-725" data-mp="3vbze6mh0iqah">
                  {loan.id} · Processor {loan.processor} · {loan.realtor}
                </p>
              </button>

              <div className="flex shrink-0 gap-2" data-id="element-726" data-mp="3liiljlsm3h8e">
                <button type="button" onClick={() => setSubject({
              id: loan.id,
              name: loan.borrower,
              context: `${loan.id} · ${loan.milestone} · ${loan.closingLabel}`,
              goalCredit: 'your consumer conversations goal'
            })} className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50 sm:flex-none" data-id="element-727" data-mp="37kll2eb1eqog">
                  <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-728" data-mp="zd8fz1nvklmg" />
                  Log update
                </button>
                <button type="button" onClick={() => setActiveLoan(loan)} className="inline-flex h-11 min-w-[7rem] items-center justify-center gap-1 rounded-lg bg-navy-800 px-3 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-729" data-mp="ucugw9494m0k">
                  View loan
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-730" data-mp="1i5avuasozxvn" />
                </button>
              </div>
            </li>)}
        </ul>
      </ListPageLayout>

      <LoanDetailDrawer loan={activeLoan} onClose={() => setActiveLoan(null)} onToast={showToast} data-id="element-731" data-mp="18bkctlfc4fnc" />
      <LogCommunicationModal subject={subject} onClose={() => setSubject(null)} onLogged={showToast} data-id="element-732" data-mp="20to9r2e38a1y" />
    </>;
}